import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Anchor, ArrowDown, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import { ProgressIndicator } from '../../components/StockIndicator';
import ForeKazikTable from './ForeKazikTable';
import ForeKazikModal from './ForeKazikModal';
import DurumModal from './DurumModal';
import { kazikCaplari, getForeKazikByInsaat } from '@mocks/materials/foreKazik';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function ForeKazik() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [filterCap, setFilterCap] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDurumOpen, setIsDurumOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    const filteredData = useMemo(() => {
        let data = getForeKazikByInsaat(insaatId);
        if (filterCap) data = data.filter(item => item.cap === parseInt(filterCap));
        return data;
    }, [insaatId, filterCap]);

    const stats = useMemo(() => {
        const toplamKazik = filteredData.length;
        const tamamlanan = filteredData.filter(s => s.durum === 'tamamlandi').length;
        const toplamMetraj = filteredData.reduce((acc, s) => acc + s.derinlik, 0);
        const testBasarili = filteredData.filter(s => s.testDurumu === 'basarili').length;
        const testOrani = toplamKazik > 0 ? Math.round((testBasarili / toplamKazik) * 100) : 0;
        return { toplamKazik, tamamlanan, toplamMetraj, testBasarili, testOrani };
    }, [filteredData]);

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleUpdateProgress = (item) => { setSelectedItem(item); setIsDurumOpen(true); };

    const handleSubmit = async (formData, isEdit) => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 800));

        if (!isEdit && formData.supplierId && formData.calculatedTotal > 0) {
            addMockCariHareket(formData.supplierId, insaatId || 'c1', {
                type: 'purchase',
                subtype: 'hakedis',
                amount: formData.calculatedTotal,
                date: new Date().toISOString().split('T')[0],
                description: `Fore Kazık — ${formData.kazikNo} (Ø${formData.cap}cm)`,
            });
        }

        toast.success(isEdit ? 'Kazık güncellendi' : 'Yeni kazık eklendi');
        setIsLoading(false);
        setIsModalOpen(false);

        if (!isEdit && formData.supplierId && formData.calculatedTotal > 0) {
            setOdemePlanData({ amount: formData.calculatedTotal, companyName: formData.tedarikci });
            setIsOdemePlanOpen(true);
        }
    };

    const handleOdemePlanConfirm = ({ nextDueDate }) => {
        toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`);
        setIsOdemePlanOpen(false);
        setOdemePlanData(null);
    };

    const handleDurumUpdate = async (durumData) => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 500));
        toast.success(`${selectedItem?.kazikNo} durumu güncellendi`);
        setIsLoading(false);
        setIsDurumOpen(false);
    };

    const statsCards = [
        { title: 'Toplam Kazık', value: `${stats.toplamKazik} adet`, icon: Anchor, color: 'primary' },
        { title: 'Tamamlanan', value: `${stats.tamamlanan} adet`, icon: CheckCircle, color: 'success' },
        { title: 'Toplam Metraj', value: `${stats.toplamMetraj} m`, icon: ArrowDown, color: 'info' },
        { title: 'Test Başarı', value: `%${stats.testOrani}`, icon: AlertCircle, color: stats.testOrani >= 90 ? 'success' : 'warning' },
    ];

    const detailFields = [
        { key: 'kazikNo', label: 'Kazık No' },
        { key: 'blok', label: 'Blok' },
        { key: 'konum', label: 'Aksis' },
        { key: 'cap', label: 'Çap (cm)' },
        { key: 'derinlik', label: 'Derinlik (m)', type: 'number' },
        { key: 'betonMiktari', label: 'Beton (m³)', type: 'number' },
        { key: 'durum', label: 'İmalat Durumu', type: 'status' },
        { key: 'testDurumu', label: 'Test Durumu', type: 'status' },
        { key: 'imalatTarihi', label: 'İmalat Tarihi', type: 'date' },
    ];

    const headerFilter = (
        <select
            value={filterCap}
            onChange={(e) => setFilterCap(e.target.value)}
            className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]"
        >
            <option value="">Tüm Çaplar</option>
            {kazikCaplari.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
    );

    return (
        <>
            <MaterialPageLayout title="Fore Kazık" subtitle="Kaba İnşaat" icon={Anchor} iconBgColor="bg-primary" backLink="/insaat/kaba" stats={statsCards} onAddClick={handleAdd} headerFilter={headerFilter}>
                <div className="p-4 border-b border-border-light bg-bg-tertiary/50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ProgressIndicator completed={stats.tamamlanan} total={stats.toplamKazik} label="İmalat İlerlemesi" />
                        <ProgressIndicator completed={stats.testBasarili} total={stats.toplamKazik} label="Test İlerlemesi" />
                    </div>
                </div>
                <ForeKazikTable data={filteredData} onView={handleView} onEdit={handleEdit} onUpdateProgress={handleUpdateProgress} />
            </MaterialPageLayout>

            <ForeKazikModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="Kazık Detayı" />
            <DurumModal isOpen={isDurumOpen} onClose={() => setIsDurumOpen(false)} onSubmit={handleDurumUpdate} data={selectedItem} isLoading={isLoading} />
            <OdemePlanMiniModal
                isOpen={isOdemePlanOpen}
                onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }}
                onConfirm={handleOdemePlanConfirm}
                amount={odemePlanData?.amount}
                companyName={odemePlanData?.companyName}
            />
        </>
    );
}
